import React, { useCallback, useEffect, useState } from 'react';
import { Mail, Send, Eye, CalendarClock, RefreshCw, Users, Loader2, Plus, Trash2 } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Separator } from '../../components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { api } from '../../App';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const HOURS = Array.from({ length: 24 }, (_, h) => h);
const MINUTES = [0, 15, 30, 45];
const ROLE_META = {
  admin: { label: 'Admins', detail: 'Full digest' },
  chairman: { label: 'Chairman', detail: 'Finance-only digest' },
  treasurer: { label: 'Treasurer', detail: 'Finance-only digest' },
};
const pad = (n) => String(n).padStart(2, '0');

/**
 * Admin Settings card: Weekly Digest Email.
 * - auto on/off, day + time (UK), recipient roles
 * - Preview (full / finance), Send test to me, Send now to everyone
 */
export const WeeklyDigestCard = ({ token }) => {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(null); // 'test' | 'all'
  const [preview, setPreview] = useState(null);
  const [previewKind, setPreviewKind] = useState('full');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newKind, setNewKind] = useState('full');
  const [testTo, setTestTo] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await api.getAuth('/api/admin/digest/settings', token);
      setCfg(d);
      setTestTo((t) => t || d.extra_recipients?.[0]?.email || '');
    } catch (e) {
      toast.error(e.message || 'Could not load digest settings');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const d = await api.putAuth('/api/admin/digest/settings', patch, token);
      setCfg(d);
      setTestTo((t) => t || d.extra_recipients?.[0]?.email || '');
      toast.success(d.message || 'Saved', { description: d.enabled ? `Next digest: ${d.next_run_uk} (UK time)` : 'Automatic sending is switched off' });
    } catch (e) {
      toast.error(e.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const openPreview = async (kind = previewKind) => {
    setPreviewKind(kind);
    setPreviewLoading(true);
    try {
      const d = await api.getAuth(`/api/admin/digest/preview?kind=${kind}`, token);
      setPreview(d);
    } catch (e) {
      toast.error(e.message || 'Could not build preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const extras = cfg?.extra_recipients || [];
  const addExtra = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { toast.error('Please enter a valid email address'); return; }
    if (extras.some((x) => x.email === email)) { toast.error('That address is already on the list'); return; }
    await save({ extra_recipients: [...extras, { email, kind: newKind }] });
    setNewEmail('');
  };
  const removeExtra = (email) => save({ extra_recipients: extras.filter((x) => x.email !== email) });
  const setExtraKind = (email, kind) => save({ extra_recipients: extras.map((x) => (x.email === email ? { ...x, kind } : x)) });
  const canSend = cfg ? (cfg.can_send ?? cfg.smtp_configured) : false;

  const sendNow = async (testOnly) => {
    if (testOnly && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(testTo.trim())) { toast.error('Enter the email address to send the test to'); return; }
    if (!testOnly && !window.confirm(`Send the digest now to ${cfg?.recipients?.length || 0} recipient(s)?`)) return;
    setSending(testOnly ? 'test' : 'all');
    try {
      const d = await api.postAuth('/api/admin/digest/send-now', testOnly ? { test_only: true, to: testTo.trim() } : { test_only: false }, token);
      toast.success(d.message || 'Sent');
      load();
    } catch (e) {
      toast.error(e.message || 'Could not send');
    } finally {
      setSending(null);
    }
  };

  if (loading) {
    return <Card data-testid="digest-card-loading"><CardContent className="p-6"><div className="h-24 animate-pulse rounded-lg bg-[hsl(var(--muted))]" /></CardContent></Card>;
  }
  if (!cfg) return null;

  return (
    <Card data-testid="digest-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <Mail className="w-5 h-5" />
          Weekly Digest Email
          <Badge className={cfg.enabled ? 'bg-[hsl(var(--gs-success))] text-white' : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]'} data-testid="digest-status-badge">
            {cfg.enabled ? 'Auto ON' : 'Auto OFF'}
          </Badge>
        </CardTitle>
        <p className="text-sm text-[hsl(var(--muted-foreground))]">
          A summary of new membership applications, ID reviews waiting, volunteers, Saturday Club, payments and refunds — sent to the people listed below so nothing waits unnoticed. The full digest covers everything; the finance-only digest covers payments, refunds, tickets, donations and Saturday Club fees.
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Auto toggle + schedule */}
        <div className="flex items-start justify-between gap-4 p-4 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.3)] flex-wrap">
          <div className="space-y-1">
            <Label className="text-base font-semibold flex items-center gap-2"><CalendarClock className="w-4 h-4" /> Send automatically</Label>
            <p className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="digest-next-run">
              {cfg.enabled ? <>Next digest: <strong>{cfg.next_run_uk}</strong> (UK time)</> : 'Automatic sending is off — you can still Preview or Send now.'}
            </p>
            {cfg.last_sent_uk && (
              <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="digest-last-sent">Last sent: {cfg.last_sent_uk} ({cfg.last_trigger}) to {cfg.last_results?.length || 0} recipient(s)</p>
            )}
          </div>
          <Switch checked={!!cfg.enabled} disabled={saving} onCheckedChange={(v) => save({ enabled: v })} aria-label="Send weekly digest automatically" data-testid="digest-enabled-switch" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="space-y-1.5">
            <Label>Day</Label>
            <Select value={String(cfg.day_of_week)} onValueChange={(v) => save({ day_of_week: Number(v) })} disabled={saving}>
              <SelectTrigger data-testid="digest-day-select"><SelectValue /></SelectTrigger>
              <SelectContent>{DAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Hour (UK time)</Label>
            <Select value={String(cfg.hour)} onValueChange={(v) => save({ hour: Number(v) })} disabled={saving}>
              <SelectTrigger data-testid="digest-hour-select"><SelectValue /></SelectTrigger>
              <SelectContent>{HOURS.map((h) => <SelectItem key={h} value={String(h)}>{pad(h)}:00</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Minute</Label>
            <Select value={String(cfg.minute)} onValueChange={(v) => save({ minute: Number(v) })} disabled={saving}>
              <SelectTrigger data-testid="digest-minute-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MINUTES.map((m) => <SelectItem key={m} value={String(m)}>:{pad(m)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <Separator />

        {/* Recipients */}
        <div className="space-y-3">
          <Label className="text-base font-semibold flex items-center gap-2"><Users className="w-4 h-4" /> Who receives it</Label>
          <div className="space-y-2 p-3 rounded-lg border border-[hsl(var(--border))]" data-testid="digest-extra-recipients">
            <p className="text-sm font-medium">Send the digest to these email addresses</p>
            {extras.length === 0 && <p className="text-xs text-[hsl(var(--muted-foreground))]">No email addresses added yet.</p>}
            {extras.map((x) => (
              <div key={x.email} className="flex flex-wrap items-center gap-2" data-testid="digest-extra-row">
                <span className="text-sm break-all flex-1 min-w-[180px]">{x.email}</span>
                <Select value={x.kind} onValueChange={(v) => setExtraKind(x.email, v)} disabled={saving}>
                  <SelectTrigger className="h-9 w-44" aria-label={`Digest type for ${x.email}`}><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="full">Full digest</SelectItem><SelectItem value="finance">Finance-only digest</SelectItem></SelectContent>
                </Select>
                <Button variant="ghost" size="sm" onClick={() => removeExtra(x.email)} disabled={saving} aria-label={`Remove ${x.email}`} data-testid="digest-extra-remove"><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') addExtra(); }} placeholder="name@example.com" className="h-9 flex-1 min-w-[200px]" data-testid="digest-extra-input" />
              <Select value={newKind} onValueChange={setNewKind}>
                <SelectTrigger className="h-9 w-44" aria-label="Digest type for the new address" data-testid="digest-extra-kind"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="full">Full digest</SelectItem><SelectItem value="finance">Finance-only digest</SelectItem></SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="h-9" onClick={addExtra} disabled={saving || !newEmail.trim()} data-testid="digest-extra-add"><Plus className="w-4 h-4 mr-1" /> Add</Button>
            </div>
          </div>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Staff accounts below send to each account's login email. Switch them off if those logins are not real mailboxes.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {Object.entries(ROLE_META).map(([role, meta]) => (
              <div key={role} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-[hsl(var(--border))]" data-testid={`digest-role-${role}`}>
                <div>
                  <p className="text-sm font-medium">{meta.label}</p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">{meta.detail}</p>
                </div>
                <Switch checked={!!cfg.roles?.[role]} disabled={saving} onCheckedChange={(v) => save({ roles: { ...cfg.roles, [role]: v } })} aria-label={`Send digest to ${meta.label}`} data-testid={`digest-role-${role}-switch`} />
              </div>
            ))}
          </div>
          <div className="text-sm text-[hsl(var(--muted-foreground))]" data-testid="digest-recipient-list">
            {cfg.recipients?.length ? (
              <>Will be sent to ({cfg.recipients.length}): {cfg.recipients.map(r => <span key={r.email} className="inline-block mr-2 mb-1 rounded-md bg-[hsl(var(--muted))] px-2 py-0.5 text-xs">{r.role === 'extra' ? r.email : `${r.name || r.email} · ${r.role} login`}{r.kind === 'finance' ? ' · finance only' : ' · full'}</span>)}</>
            ) : <span className="text-amber-700">No recipients — add an email address above, or switch on a staff account.</span>}
          </div>
        </div>

        <Separator />

        {/* Actions */}
        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" onClick={() => openPreview('full')} disabled={previewLoading} data-testid="digest-preview-button">
            {previewLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />} Preview
          </Button>
          <Input type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Send a test to…" className="h-9 w-56" aria-label="Send a test digest to this address" data-testid="digest-test-to" />
          <Button variant="outline" onClick={() => sendNow(true)} disabled={!!sending || !canSend || !testTo.trim()} data-testid="digest-send-test-button">
            {sending === 'test' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send test
          </Button>
          <Button onClick={() => sendNow(false)} disabled={!!sending || !canSend || !cfg.recipients?.length} className="bg-[hsl(var(--gs-navy))] text-white hover:bg-[hsl(var(--gs-navy-2))]" data-testid="digest-send-now-button">
            {sending === 'all' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send now to all
          </Button>
          <Button variant="ghost" size="sm" onClick={load} aria-label="Refresh" data-testid="digest-refresh-button"><RefreshCw className="w-4 h-4" /></Button>
          {!canSend && <p className="text-xs text-amber-700">Email sending is not set up — configure Email Delivery above before sending.</p>}
        </div>
      </CardContent>

      {/* Preview dialog */}
      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) setPreview(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="digest-preview-dialog">
          <DialogHeader>
            <DialogTitle>Digest preview</DialogTitle>
            <DialogDescription>
              {preview?.period?.label} · Subject: <span className="font-medium text-[hsl(var(--foreground))]" data-testid="digest-preview-subject">{preview?.subject}</span>
            </DialogDescription>
          </DialogHeader>
          <Tabs value={previewKind} onValueChange={(v) => openPreview(v)}>
            <TabsList>
              <TabsTrigger value="full" data-testid="digest-preview-tab-full">Full digest</TabsTrigger>
              <TabsTrigger value="finance" data-testid="digest-preview-tab-finance">Finance-only digest</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="flex-1 min-h-0 rounded-lg border border-[hsl(var(--border))] overflow-hidden bg-[hsl(var(--muted)/0.3)]">
            {preview && (
              <iframe title="Digest preview" srcDoc={preview.html} sandbox="" className="w-full h-[62vh] bg-white" data-testid="digest-preview-frame" />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
